"use client";

import type React from "react";
import { TriangleAlert, KeyRound } from "lucide-react";
import { DataChip } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";
import type { VariableRefMeta, VariableRefStatus } from "./variableRefStatus";

interface VariableNotFoundChipProps {
  size?: "default" | "sm";
  className?: string;
  textClassName?: string;
}

export function VariableNotFoundChip({
  size = "default",
  className,
  textClassName = "text-sm",
}: VariableNotFoundChipProps) {
  const sm = size === "sm";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border border-warning-400/70 bg-warning-50 text-warning-700 select-none leading-none",
        textClassName,
        sm ? "px-1.5 py-0.5" : "px-2 py-1",
        className
      )}
    >
      <TriangleAlert className={cn("shrink-0", sm ? "size-3" : "size-3.5")} />
      <span className="truncate max-w-[140px]">Variable not found</span>
    </span>
  );
}

interface VariableRefChipProps {
  status: VariableRefStatus;
  size?: "default" | "sm";
  bare?: boolean;
  className?: string;
  showSource?: boolean;
  textClassName?: string;
  relaxedWidth?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
}

function metaToChipProps(meta: VariableRefMeta) {
  return {
    sourceImage: meta.sourceImage,
    sourceIconName: meta.sourceIconName,
    sourceColor: meta.sourceColor,
    sourceLabel: meta.sourceLabel,
    dataType: meta.dataType,
    variableLabel: meta.variableLabel,
    isList: meta.isList,
    sourceIcon: meta.isSecret ? KeyRound : undefined,
  };
}

export function VariableRefChip({
  status,
  size = "default",
  bare = false,
  className,
  showSource = true,
  textClassName = "text-sm",
  relaxedWidth = false,
  onMouseEnter,
  onMouseLeave,
}: VariableRefChipProps) {
  if (status.kind === "not_found") {
    return (
      <VariableNotFoundChip
        size={size}
        className={className}
        textClassName={textClassName}
      />
    );
  }

  return (
    <DataChip
      {...metaToChipProps(status.meta!)}
      size={size}
      bare={bare}
      showSource={showSource}
      warning={status.kind === "unreachable"}
      className={className}
      textClassName={textClassName}
      relaxedWidth={relaxedWidth}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );
}
